<template>
  <div class="public-memorial">
    <v-container class="py-10" max-width="960">
      <div class="page-header mb-2">
        <!-- Brand Header -->
        <div class="brand-header mb-6">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu v-if="isAuthenticated" />

        <!-- Memorial Hero Section -->
        <div class="memorial-hero">
          <!-- Centered Profile Photo -->
          <div class="hero-photo-container">
            <img
              v-if="memorial?.profilePhoto"
              :src="profilePhotoUrl"
              :alt="memorial?.nameOfPerson"
              class="hero-photo"
            />
            <div v-else class="hero-photo-placeholder">
              <v-icon size="120" color="grey-lighten-1">mdi-account</v-icon>
            </div>
          </div>

          <!-- Memorial Name -->
          <h1 class="hero-name">
            {{ memorial?.nameOfPerson || (loading ? 'Loading memory…' : 'Memory') }}
            <span v-if="memorial?.birthDate && memorial?.deathDate" class="life-dates">
              {{ formatLifeDate(memorial.birthDate) }} - {{ formatLifeDate(memorial.deathDate) }}
            </span>
          </h1>

          <!-- Date and Details -->
          <div v-if="memorial?.birthDate || memorial?.deathDate || (hasEvent && memorial?.memorialDate)" class="hero-details">
            <div v-if="memorial?.birthDate && !memorial?.deathDate" class="hero-detail-item">
              <v-icon size="20" color="grey-darken-1" class="mr-2">mdi-cake-variant-outline</v-icon>
              <span>Born {{ formatLifeDate(memorial.birthDate) }}</span>
            </div>
            <div v-if="memorial?.deathDate && !memorial?.birthDate" class="hero-detail-item">
              <v-icon size="20" color="grey-darken-1" class="mr-2">mdi-flower-outline</v-icon>
              <span>Died {{ formatLifeDate(memorial.deathDate) }}</span>
            </div>
            <div v-if="hasEvent && memorial?.memorialDate" class="hero-detail-item">
              <v-icon size="20" color="grey-darken-1" class="mr-2">mdi-calendar</v-icon>
              <span>{{ formatMemorialDate(memorial.memorialDate) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Memorial Date Alert -->
      <v-alert
        v-if="hasEvent && memorial?.memorialDate && isMemorialDatePassed"
        type="info"
        variant="tonal"
        class="mb-6"
      >
        <div class="d-flex align-start">
          <v-icon class="mr-3">mdi-calendar-clock</v-icon>
          <div>
            <div class="font-weight-medium mb-1">Memory Date Has Passed</div>
            <div class="text-body-2">
              The memory for {{ memorial?.nameOfPerson }} was on {{ formatMemorialDate(memorial?.memorialDate) }}.
              You can still contribute your memories and the loved ones will be notified of your update.
            </div>
          </div>
        </div>
      </v-alert>

      <v-card v-if="!isAuthenticated" class="main-card return-visitor-card mb-6" elevation="0">
        <v-card-text class="d-flex flex-column flex-md-row align-md-center justify-space-between">
          <div>
            <h3 class="text-h6 mb-1">Already contributed?</h3>
            <p class="text-body-2 text--secondary mb-0">
              Sign in to view or update your memories for {{ memorial?.nameOfPerson || 'this memory' }}.
            </p>
          </div>
          <v-btn
            color="primary"
            class="mt-3 mt-md-0"
            append-icon="mdi-login"
            @click="goToLogin"
          >
            Log in to see contribution
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card v-if="isAuthenticated && userContribution" class="main-card mb-6" elevation="0">
        <v-card-text>
          <v-alert type="success" variant="tonal">
            You're viewing your contribution to this memory. You can update your message and photos below.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>

      <v-card
        v-if="!loading && error"
        class="main-card error-card mb-6"
        elevation="0"
      >
        <v-card-text class="text-center">
          <v-icon color="primary" size="48" class="mb-3">mdi-information-outline</v-icon>
          <h2 class="text-h5 mb-2">We couldn’t open this memory</h2>
          <p class="text-body-1 text--secondary">
            {{ error }}
          </p>
        </v-card-text>
      </v-card>

      <template v-else>
        <!-- RSVP Section -->
        <v-card elevation="0" class="mb-6 rsvp-card main-card">
          <v-card-title class="section-title d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon left color="primary">mdi-calendar-heart</v-icon>
              RSVP
            </div>
            <v-btn color="primary" variant="text" class="rsvp-toggle" @click="toggleRsvp" :disabled="rsvpSubmitting || rsvpSuccess">
              <v-icon left>mdi-chevron-{{ rsvpExpanded ? 'up' : 'down' }}</v-icon>
              Let us know
            </v-btn>
          </v-card-title>
          <v-expand-transition>
            <v-card-text v-if="rsvpExpanded">
              <!-- Show existing RSVP if user has responded -->
              <v-alert v-if="userRsvp" type="info" variant="tonal" class="mb-4">
                <strong>RSVP sent {{ formatRsvpDate(userRsvp.dateCreated) }}</strong>
              </v-alert>

              <p class="text-body-2 text--secondary mb-4">
                <span v-if="userRsvp">Update your response below:</span>
                <span v-else>We'd love to know if you can join us to remember {{ memorial?.nameOfPerson || 'them' }}.</span>
              </p>

              <v-row v-if="!isAuthenticated" class="mb-4">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="contributorName"
                    label="Your name"
                    variant="outlined"
                    density="comfortable"
                    :disabled="rsvpSubmitting || rsvpSuccess"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="contributorEmail"
                    label="Email address"
                    type="email"
                    variant="outlined"
                    density="comfortable"
                    :disabled="rsvpSubmitting || rsvpSuccess"
                    :error="rsvpEmailError"
                    :error-messages="rsvpEmailError ? 'Please enter a valid email address' : ''"
                    @blur="validateRsvpEmail"
                    @input="rsvpEmailError = false"
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="rsvp-actions mb-4">
                <v-btn-toggle
                  v-model="rsvpStatus"
                  mandatory
                  class="rsvp-toggle-group"
                  :disabled="rsvpSubmitting || rsvpSuccess"
                >
                  <v-btn
                    v-for="option in rsvpOptions"
                    :key="option.value"
                    :value="option.value"
                    class="rsvp-option-btn"
                    :ripple="false"
                    color="primary"
                    :variant="rsvpStatus === option.value ? 'elevated' : 'tonal'"
                  >
                    <v-icon size="18" class="mr-2">
                      {{ rsvpStatus === option.value ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    {{ option.label }}
                  </v-btn>
                </v-btn-toggle>
              </div>

              <v-divider class="mb-4"></v-divider>

              <h4 class="text-h6 mb-2">Leave a private note</h4>
              <v-textarea
                v-model="rsvpMessage"
                label="Share a thought or message"
                variant="outlined"
                rows="4"
                auto-grow
                :disabled="rsvpSubmitting || rsvpSuccess"
                class="mb-2"
              ></v-textarea>

              <p class="text-caption text--secondary mb-4">
                Your response and note are not seen by anyone other than the sender
              </p>

              <v-alert v-if="rsvpSuccess" type="success" variant="tonal" class="mb-4">
                {{ rsvpResponse || 'Thank you for letting us know.' }}
              </v-alert>
              <v-alert v-if="rsvpError" type="error" variant="tonal" class="mb-4">
                {{ rsvpError }}
              </v-alert>

              <v-btn
                color="primary"
                :loading="rsvpSubmitting"
                :disabled="!rsvpStatus || rsvpSubmitting || rsvpSuccess"
                @click="sendRsvp"
              >
                {{ userRsvp ? 'Send Updated Response' : 'Send Now' }}
              </v-btn>

              <div class="text-center mt-4">
                <v-btn variant="text" color="primary" @click="toggleRsvp" :disabled="rsvpSubmitting">
                  Close
                </v-btn>
              </div>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Host Message / Video -->
        <v-card elevation="0" class="mb-6 message-card main-card">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-play-circle</v-icon>
            Message from the host
          </v-card-title>
          <v-card-text>
            <div v-if="hostVideoUrl" class="host-video-wrapper">
              <video :src="hostVideoUrl" controls preload="metadata"></video>
            </div>
            <div v-else-if="memorial?.hostMessage" class="host-message">
              <p class="text-body-1">{{ memorial.hostMessage }}</p>
            </div>
            <div v-else class="host-message placeholder">
              <p class="text-body-2 text--secondary">The host is preparing a message for this memory.</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Contribution Section -->
        <v-card elevation="0" class="contribute-card main-card mb-6">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-heart-plus</v-icon>
            Contribute
          </v-card-title>
          <v-card-text>
            <p class="text-body-1 text--secondary mb-4 contribute-intro">
              Share your memories, photos, and stories to celebrate the life of {{ memorial?.nameOfPerson || 'your loved one' }}.
            </p>

            <v-form ref="contributionForm">
              <v-row v-if="!isAuthenticated">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="contributorName"
                    label="Your name"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                  <p class="text-caption text--secondary mb-0">
                    Add a name so your public contribution shows your name instead of your email.
                  </p>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="contributorEmail"
                    label="Email address"
                    variant="outlined"
                    density="comfortable"
                    :error="contributionEmailError"
                    :error-messages="contributionEmailError ? ['Enter a valid email address'] : []"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-textarea
                v-model="contributorMessage"
                label="Write a message"
                variant="outlined"
                rows="4"
                auto-grow
                class="mb-2"
              ></v-textarea>
              <p class="text-caption text--secondary mb-4">
                Share your thoughts or memories about {{ memorial?.nameOfPerson || 'your loved one' }}. This will appear alongside the memory.
              </p>

              <div class="upload-area mb-4" @dragover.prevent @drop.prevent="handleDrop">
                <button type="button" class="upload-button" @click="triggerFileSelect">
                  <v-icon size="24" class="mr-2">mdi-camera</v-icon>
                  Add photo
                </button>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*,.heic,.HEIC"
                  multiple
                  class="upload-hidden-input"
                  @change="onManualFileSelect"
                />
                <p class="text-caption text--secondary mt-2 mb-0">Drag photos here or tap the button to upload.</p>
              </div>

              <div v-if="contributionPhotos.length" class="photo-preview-list mb-4">
                <div
                  v-for="(photo, index) in contributionPhotos"
                  :key="photo.id"
                  class="photo-preview-row"
                >
                  <div class="preview-thumb">
                    <img v-if="photo.preview" :src="photo.preview" alt="Uploaded photo" />
                  </div>
                  <div class="preview-details">
                    <v-textarea
                      v-model="photo.caption"
                      label="Add a story about this photo"
                      variant="outlined"
                      rows="2"
                      auto-grow
                    ></v-textarea>
                  </div>
                  <div class="preview-actions">
                    <v-btn variant="text" color="error" size="small" @click="removePhoto(index)">
                      Remove
                    </v-btn>
                  </div>
                </div>
              </div>

              <div v-if="existingContributionAudios.length" class="mb-5">
                <div v-for="(audio, index) in existingContributionAudios" :key="audio.uuid" class="mb-4">
                  <AudioEntry :media="audio" :memorial-slug="slug" />
                  <v-btn color="error" variant="text" size="small" prepend-icon="mdi-delete" @click="removeExistingAudio(index)">
                    Remove voice memory
                  </v-btn>
                </div>
              </div>

              <div class="mb-5">
                <h4 class="text-h6 mb-2">Add a voice memory</h4>
                <VoiceContributionInput v-model="contributionAudios" />
              </div>

              <div class="contribution-actions">
                <v-alert v-if="contributionError" type="error" variant="tonal" class="mb-4">
                  {{ contributionError }}
                </v-alert>

                <v-alert v-if="contributionSuccess" type="success" variant="tonal" class="mb-4">
                  {{ contributionSuccessMessage }}
                </v-alert>

                <v-spacer></v-spacer>

                <v-btn
                  color="primary"
                  size="large"
                  class="px-6"
                  :disabled="contributionSubmitting"
                  :loading="contributionSubmitting"
                  @click="openContributionDialog"
                >
                  {{ userContribution ? 'Update Contribution' : 'Save & Contribute Now' }}
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Donation Section -->
        <v-card id="donation-section" v-if="memorial?.memorialFunds && memorial.memorialFunds.length > 0" elevation="0" class="donation-card main-card mb-6">
          <v-card-title class="section-title">
            <v-icon left color="primary">mdi-currency-usd</v-icon>
            {{ memorial.memorialFunds[0].title }}
          </v-card-title>
          <v-card-text>
            <div class="donation-intro mb-4 d-flex align-start" :class="{ 'mt-4': isAuthenticated }">
              <img
                v-if="profilePhotoUrl"
                :src="profilePhotoUrl"
                :alt="memorial?.nameOfPerson"
                class="donation-profile-photo mr-3"
              />
              <div class="flex-grow-1">
                <p class="text-body-1 text--secondary mb-3">
                  {{ memorial?.nameOfPerson }} would like any donations to go to
                  <template v-if="memorial.memorialFunds.length === 1">
                    <strong>{{ memorial.memorialFunds[0].title }}</strong>.
                  </template>
                  <template v-else>
                    <strong>{{ memorial.memorialFunds.map(f => f.title).join(', ') }}</strong>.
                  </template>
                  If you would like to donate to {{ memorial.memorialFunds.length > 1 ? 'these' : 'this' }},
                  use the card payment below and PassingOn will collect and pay on your behalf
                  (note there is a processing fee), or you can go to their website and donate directly.
                </p>

                <!-- List existing donations if user has any -->
                <div v-if="userDonations && userDonations.length > 0" class="mb-3">
                  <p class="text-subtitle-2 font-weight-medium mb-2">Your donations:</p>
                  <v-list density="compact" class="pa-0">
                    <v-list-item v-for="donation in userDonations" :key="donation.id" class="px-0">
                      <template v-slot:prepend>
                        <v-icon size="small" color="success" class="mr-2">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title class="text-body-2">
                        ${{ donation.amount }} on {{ formatDate(donation.dateDonated) }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </div>
              </div>
            </div>

            <!-- Show "Donate Again" button if user has donations and form is hidden -->
            <div v-if="userDonations && userDonations.length > 0 && !showDonationForm" class="text-center mb-4">
              <v-btn
                color="primary"
                variant="tonal"
                size="large"
                @click="showDonationForm = true"
              >
                <v-icon left>mdi-currency-usd</v-icon>
                Donate Again
              </v-btn>
            </div>

            <!-- Donation Closed Message (if more than 1 month past memorial date) -->
            <v-alert
              v-if="isDonationPeriodClosed"
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <div class="d-flex align-start">
                <v-icon class="mr-3">mdi-calendar-remove</v-icon>
                <div>
                  <div class="font-weight-medium mb-1">Donation Period Has Closed</div>
                  <div class="text-body-2">
                    It has been more than one month since the memory date. The donation period for this memory has now closed.
                    You can still share your memories and contributions above.
                  </div>
                </div>
              </div>
            </v-alert>

            <v-form v-show="!isDonationPeriodClosed && (!userDonations || userDonations.length === 0 || showDonationForm)">
              <!-- Amount Selection -->
              <div class="mb-4">
                <label class="text-subtitle-1 font-weight-medium mb-2 d-block">Donation Amount</label>
                <v-btn-toggle
                  v-model="donationPresetAmount"
                  mandatory
                  color="primary"
                  class="mb-3"
                  @update:model-value="onPresetAmountChange"
                >
                  <v-btn value="5">$5</v-btn>
                  <v-btn value="10">$10</v-btn>
                  <v-btn value="15">$15</v-btn>
                  <v-btn value="20">$20</v-btn>
                  <v-btn value="other">Other</v-btn>
                </v-btn-toggle>

                <v-text-field
                  v-if="donationPresetAmount === 'other'"
                  v-model="donationCustomAmount"
                  label="Enter custom amount"
                  variant="outlined"
                  prefix="$"
                  type="number"
                  min="1"
                  step="0.01"
                  density="comfortable"
                  class="mt-2"
                ></v-text-field>
              </div>

              <!-- Anonymous Checkbox -->
              <v-checkbox
                v-model="donationAnonymous"
                label="Make this donation anonymous"
                class="mb-4"
              >
                <template v-slot:label>
                  <div>
                    <div>Make this donation anonymous</div>
                    <div class="text-caption text--secondary">
                      If you wish your donation to remain anonymous then check this here.
                      For donations that are not anonymous, the organiser will be able to see your name and amount.
                    </div>
                  </div>
                </template>
              </v-checkbox>

              <!-- Stripe Card Element -->
              <div class="mb-4">
                <label class="text-subtitle-1 font-weight-medium mb-2 d-block">Payment Information</label>
                <div id="donation-card-element" class="stripe-card-element"></div>
              </div>

              <!-- Donate Button -->
              <v-btn
                color="primary"
                size="large"
                block
                :loading="donationProcessing"
                :disabled="!donationAmount || donationProcessing"
                @click="processDonation"
              >
                Donate ${{ donationAmount }}
              </v-btn>

              <v-alert v-if="donationError" type="error" variant="tonal" class="mt-3">
                {{ donationError }}
              </v-alert>
            </v-form>

            <!-- Success Message - only show after new donation -->
            <v-alert v-if="donationSuccess" type="success" variant="tonal">
              <div class="mb-2">
                <strong>Thank you for your donation!</strong>
              </div>
              <div class="text-body-2">
                Your donation of ${{ donationAmount }} has been processed successfully.
              </div>
              <div class="text-caption mt-2">
                Transaction ID: {{ donationTransactionId }}
              </div>
            </v-alert>
          </v-card-text>
        </v-card>
      </template>
    </v-container>

    <!-- Contribution Signup & Verification Dialog -->
    <v-dialog v-model="showContributionDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">
          <template v-if="contributionDialogStep === 'credentials'">
            Create your account
          </template>
          <template v-else-if="contributionDialogStep === 'verify'">
            Verify your email
          </template>
          <template v-else>
            Contribution submitted
          </template>
        </v-card-title>
        <v-card-text>
          <template v-if="contributionDialogStep === 'credentials'">
            <p class="text-body-2 mb-4">
              We create a secure account so you can update your contribution later. Enter a password and we’ll send a verification code to {{ contributorEmail || 'your email' }}.
            </p>
            <v-text-field
              v-model="contributorPassword"
              label="Create password"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
            <v-text-field
              v-model="contributorPasswordConfirm"
              label="Confirm password"
              :type="showPasswordConfirm ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
            ></v-text-field>
            <v-alert v-if="contributionDialogError" type="error" variant="tonal" class="mt-2">
              {{ contributionDialogError }}
            </v-alert>
          </template>

          <template v-else-if="contributionDialogStep === 'verify'">
            <p class="text-body-2 mb-4">
              Enter the verification code we sent to {{ contributorEmail }}.
            </p>
            <v-text-field
              v-model="verificationCode"
              label="Verification code"
              variant="outlined"
              density="comfortable"
              maxlength="6"
            ></v-text-field>
            <v-btn
              variant="text"
              color="secondary"
              size="small"
              :loading="resendingCode"
              :disabled="resendCooldown > 0 || resendingCode"
              @click="resendContributionCode"
            >
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code' }}
            </v-btn>
            <v-alert v-if="contributionDialogError" type="error" variant="tonal" class="mt-2">
              {{ contributionDialogError }}
            </v-alert>
          </template>

          <template v-else>
            <v-alert type="success" variant="tonal" class="mb-2">
              {{ contributionCompletedMessage || 'Thank you for contributing. We have saved your memories.' }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="contributionDialogStep === 'credentials'">
            <v-btn variant="text" @click="closeContributionDialog" :disabled="signupLoading">Cancel</v-btn>
            <v-btn color="primary" :loading="signupLoading" @click="startContributionSignup">
              Continue
            </v-btn>
          </template>
          <template v-else-if="contributionDialogStep === 'verify'">
            <v-btn variant="text" @click="closeContributionDialog" :disabled="verifyingCode || contributionSaving">Cancel</v-btn>
            <v-btn color="primary" :loading="verifyingCode || contributionSaving" @click="verifyContributionCode">
              Verify & Submit
            </v-btn>
          </template>
          <template v-else>
            <v-btn color="primary" @click="closeContributionDialog">Close</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Donation Authentication Dialog -->
    <v-dialog v-model="showDonationDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">
          <template v-if="donationDialogStep === 'credentials'">
            Complete your donation
          </template>
          <template v-else-if="donationDialogStep === 'login'">
            Login to donate
          </template>
          <template v-else-if="donationDialogStep === 'verify'">
            Verify your email
          </template>
        </v-card-title>
        <v-card-text>
          <template v-if="donationDialogStep === 'credentials'">
            <p class="text-body-2 mb-4">
              To complete your donation, please provide your details and create an account.
            </p>
            <div class="text-center mb-4">
              <v-btn variant="text" size="small" @click="donationDialogStep = 'login'">
                Sign In
              </v-btn>
            </div>
            <v-text-field
              v-model="donorName"
              label="Your name"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
            <v-text-field
              v-model="donorEmail"
              label="Email address"
              type="email"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
            <v-text-field
              v-model="donorPassword"
              label="Create password"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
            <v-text-field
              v-model="donorPasswordConfirm"
              label="Confirm password"
              :type="showPasswordConfirm ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
            ></v-text-field>
            <v-alert v-if="donationDialogError" type="error" variant="tonal" class="mt-2">
              {{ donationDialogError }}
            </v-alert>
          </template>

          <template v-else-if="donationDialogStep === 'login'">
            <p class="text-body-2 mb-4">
              Login with your existing account to complete your donation.
            </p>
            <v-text-field
              v-model="donorEmail"
              label="Email address"
              type="email"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
            <v-text-field
              v-model="donorPassword"
              label="Password"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              density="comfortable"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
            <v-alert v-if="donationDialogError" type="error" variant="tonal" class="mt-2">
              {{ donationDialogError }}
            </v-alert>
            <div class="text-center mt-4">
              <v-btn variant="text" size="small" @click="donationDialogStep = 'credentials'">
                Need an account? Sign up
              </v-btn>
            </div>
          </template>

          <template v-else-if="donationDialogStep === 'verify'">
            <p class="text-body-2 mb-4">
              Enter the verification code we sent to {{ donorEmail }}.
            </p>
            <v-text-field
              v-model="donorVerificationCode"
              label="Verification code"
              variant="outlined"
              density="comfortable"
              maxlength="6"
            ></v-text-field>
            <v-btn
              variant="text"
              color="secondary"
              size="small"
              :loading="resendingCode"
              :disabled="resendCooldown > 0 || resendingCode"
              @click="resendDonorCode"
            >
              {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code' }}
            </v-btn>
            <v-alert v-if="donationDialogError" type="error" variant="tonal" class="mt-2">
              {{ donationDialogError }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="donationDialogStep === 'credentials'">
            <v-btn variant="text" @click="closeDonationDialog" :disabled="donorSignupLoading">Cancel</v-btn>
            <v-btn color="primary" :loading="donorSignupLoading" @click="startDonorSignup">
              Continue
            </v-btn>
          </template>
          <template v-else-if="donationDialogStep === 'login'">
            <v-btn variant="text" @click="closeDonationDialog" :disabled="donorSignupLoading">Cancel</v-btn>
            <v-btn color="primary" :loading="donorSignupLoading" @click="loginDonor">
              Login
            </v-btn>
          </template>
          <template v-else-if="donationDialogStep === 'verify'">
            <v-btn variant="text" @click="closeDonationDialog" :disabled="donorVerifyingCode || donationProcessing">Cancel</v-btn>
            <v-btn color="primary" :loading="donorVerifyingCode || donationProcessing" @click="verifyDonorCode">
              Verify & Donate
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile } from '../services/photoUpload'
import ProfileMenu from '../components/ProfileMenu.vue'
import AudioEntry from '../components/media/AudioEntry.vue'
import VoiceContributionInput from '../components/media/VoiceContributionInput.vue'
import authService from '../services/auth'
import { useRouter } from 'vue-router'
import { loadStripe } from '@stripe/stripe-js'
import { formatLifeDate, sleep } from '../utils/helpers'

const router = useRouter()

const route = useRoute()
const slug = route.params.slug
const stayLoggedIn = route.query.sli === 'true'

const loading = ref(true)
const error = ref('')
const memorial = ref(null)
const userContribution = ref(null)
const userRsvp = ref(null)
const userDonations = ref(null)

const rsvpExpanded = ref(true)
const rsvpStatus = ref('')
const rsvpMessage = ref('')
const rsvpSubmitting = ref(false)
const rsvpSuccess = ref(false)
const rsvpResponse = ref('')
const rsvpError = ref('')

const rsvpOptions = [
  { label: 'I will be attending', value: 'ATTENDING' },
  { label: 'I will not be attending', value: 'NOT_ATTENDING' },
  { label: "I'm not sure at the moment", value: 'UNSURE' }
]

const contributorName = ref('')
const contributorEmail = ref('')
const contributorMessage = ref('')
const rsvpEmailError = ref(false)
const contributionForm = ref(null)
const contributionPhotos = ref([])
const contributionAudios = ref([])
const existingContributionAudios = ref([])
const removedPhotoIds = ref([]) // Track UUIDs of removed existing photos
const selectedFiles = ref([])
const fileInput = ref(null)
const contributionError = ref('')
const contributionSuccess = ref(false)
const contributionSuccessMessage = ref('Thank you for sharing your memories.')
const contributionSubmitting = ref(false)

const showContributionDialog = ref(false)
const contributionDialogStep = ref('credentials')
const contributorPassword = ref('')
const contributorPasswordConfirm = ref('')
const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const contributionDialogError = ref('')
const signupToken = ref('')
const signupLoading = ref(false)
const verificationCode = ref('')
const verifyingCode = ref(false)
const contributionSaving = ref(false)
const contributionCompletedMessage = ref('')
const resendingCode = ref(false)
const resendCooldown = ref(0)
let resendTimer = null

// Donation refs
const donationPresetAmount = ref('10')
const donationCustomAmount = ref('')
const donationAnonymous = ref(false)
const donationProcessing = ref(false)
const donationSuccess = ref(false)
const donationError = ref('')
const donationTransactionId = ref('')
const donationStripeInstance = ref(null)
const donationCardElement = ref(null)
const showDonationForm = ref(false)
const showDonationDialog = ref(false)
const donationDialogStep = ref('credentials')
const donationDialogError = ref('')
const donorName = ref('')
const donorEmail = ref('')
const donorPassword = ref('')
const donorPasswordConfirm = ref('')
const donorVerificationCode = ref('')
const donorSignupToken = ref('')
const donorSignupLoading = ref(false)
const donorVerifyingCode = ref(false)
const donorPaymentMethod = ref(null)

const donationAmount = computed(() => {
  if (donationPresetAmount.value === 'other') {
    return donationCustomAmount.value ? parseFloat(donationCustomAmount.value).toFixed(2) : '0.00'
  }
  return donationPresetAmount.value
})

const isAuthenticated = computed(() => {
  return !!apiService.getToken()
})

const hostVideoUrl = computed(() => {
  const video = memorial.value?.hostVideoMessage
  if (!video || !video.location || !video.filename || !video.extension) return ''
  return `${video.location}/${video.filename}.${video.extension}`
})

const profilePhotoUrl = computed(() => {
  const photo = memorial.value?.profilePhoto
  if (!photo || !photo.location || !photo.filename || !photo.extension) return ''
  return `${photo.location}/${photo.filename}.${photo.extension}`
})

const contributionEmailError = computed(() => {
  if (!contributorEmail.value) return false
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return !emailPattern.test(contributorEmail.value)
})

// Memorial date status computed properties
// Memorials created before events became optional have no flag and always had one
const hasEvent = computed(() => {
  return memorial.value?.hasEvent !== false
})

const isMemorialDatePassed = computed(() => {
  if (!memorial.value?.memorialDate) return false
  const memorialDate = new Date(memorial.value.memorialDate)
  const now = new Date()
  return memorialDate < now
})

const isDonationPeriodClosed = computed(() => {
  if (!memorial.value?.memorialDate) return false
  const memorialDate = new Date(memorial.value.memorialDate)
  const now = new Date()
  const oneMonthLater = new Date(memorialDate)
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
  return now > oneMonthLater
})

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatMemorialDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const handleMemorialResponse = (response) => {
  if (response.success) {
    memorial.value = response.memorial
    userContribution.value = response.userContribution || null
    userRsvp.value = response.userRsvp || null
    userDonations.value = response.userDonations || null

    // Clear existing form data
    contributorName.value = ''
    contributorEmail.value = ''
    contributorMessage.value = ''
    contributionPhotos.value = []
    contributionAudios.value = []
    existingContributionAudios.value = []
    removedPhotoIds.value = [] // Clear removed photo tracking

    // Pre-fill RSVP form with user's existing RSVP if available
    if (userRsvp.value) {
      rsvpStatus.value = userRsvp.value.status || ''
      rsvpMessage.value = userRsvp.value.message || ''
    } else {
      rsvpStatus.value = ''
      rsvpMessage.value = ''
    }

    // Pre-fill the form with user's existing contribution if available
    if (userContribution.value) {
      contributorName.value = userContribution.value.contributorName || ''
      contributorEmail.value = userContribution.value.contributorEmail || ''
      contributorMessage.value = userContribution.value.message || ''

      // Load existing photos if any
      if (userContribution.value.media && userContribution.value.media.length > 0) {
        userContribution.value.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
          const photoUrl = `${media.location}/${media.filename}.${media.extension}`
          contributionPhotos.value.push({
            id: media.uuid,
            caption: media.title || '',
            preview: photoUrl,
            existing: true // Mark as existing photo (not new upload)
          })
        })
        existingContributionAudios.value = userContribution.value.media.filter(media => media.mediaType === 4)
      }
    }
  } else {
    error.value = response.msg || 'This memory may have been removed or the link might be incorrect. Please check with the person who shared it.'
  }
}

const toggleRsvp = () => {
  rsvpExpanded.value = !rsvpExpanded.value
}

const formatRsvpDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'today'
  } else if (diffDays === 1) {
    return 'yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7)
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30)
    return months === 1 ? '1 month ago' : `${months} months ago`
  } else {
    const years = Math.floor(diffDays / 365)
    return years === 1 ? '1 year ago' : `${years} years ago`
  }
}

const validateRsvpEmail = () => {
  if (!contributorEmail.value) {
    rsvpEmailError.value = false
    return
  }
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  rsvpEmailError.value = !emailPattern.test(contributorEmail.value)
}

const sendRsvp = async () => {
  if (!memorial.value?.shareSlug || !rsvpStatus.value) return

  // Validate email for non-authenticated users
  if (!isAuthenticated.value && contributorEmail.value) {
    validateRsvpEmail()
    if (rsvpEmailError.value) {
      rsvpError.value = 'Please enter a valid email address'
      return
    }
  }

  rsvpSubmitting.value = true
  rsvpError.value = ''
  try {
    const payload = {
      name: contributorName.value || '',
      email: contributorEmail.value || '',
      status: rsvpStatus.value,
      message: rsvpMessage.value || ''
    }
    const response = await apiService.submitRsvp(memorial.value.shareSlug, payload)
    if (response.success) {
      rsvpSuccess.value = true
      rsvpResponse.value = response.msg || 'Thank you for letting us know.'
    } else {
      rsvpError.value = response.msg || 'We could not record your RSVP. Please try again.'
    }
  } catch (err) {
    console.error('RSVP error:', err)
    rsvpError.value = 'We could not record your RSVP. Please try again.'
  } finally {
    rsvpSubmitting.value = false
  }
}

const onPhotoSelect = async (files) => {
  const incoming = Array.from(files || [])
  if (!incoming.length) {
    return
  }

  for (const file of incoming) {
    if (!isSupportedImage(file)) continue

    try {
      const processedFile = await normalizeImageFile(file)
      const preview = URL.createObjectURL(processedFile)
      contributionPhotos.value.push({
        id: `${processedFile.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: processedFile,
        caption: '',
        preview
      })
    } catch (error) {
      console.error('Error processing file:', error)
    }
  }

  selectedFiles.value = contributionPhotos.value.map(photo => photo.file)
}

const triggerFileSelect = () => {
  fileInput.value?.click()
}

const onManualFileSelect = (event) => {
  const files = event.target.files || []
  onPhotoSelect(files)
  event.target.value = ''
}

const removePhoto = (index) => {
  const photo = contributionPhotos.value.splice(index, 1)[0]

  // If it's an existing photo (has id), track it for removal
  if (photo?.existing && photo?.id) {
    removedPhotoIds.value.push(photo.id)
  }

  // Clean up preview URL for newly uploaded photos
  if (photo?.preview && !photo?.existing) {
    URL.revokeObjectURL(photo.preview)
  }

  selectedFiles.value = contributionPhotos.value.filter(item => !item.existing).map(item => item.file)
}

const removeExistingAudio = index => {
  const audio = existingContributionAudios.value.splice(index, 1)[0]
  if (audio?.uuid) removedPhotoIds.value.push(audio.uuid)
}

const handleDrop = (event) => {
  const files = Array.from(event.dataTransfer?.files || []).filter(file => isSupportedImage(file))
  if (files.length) {
    onPhotoSelect(files)
  }
}

const goToLogin = () => {
  router.push({ name: 'Login', query: { redirect: `/for/${slug}` } })
}

const clearContributionForm = () => {
  contributorName.value = ''
  contributorEmail.value = ''
  contributorMessage.value = ''
  contributionPhotos.value.forEach(photo => {
    if (photo.preview) URL.revokeObjectURL(photo.preview)
  })
  contributionPhotos.value = []
  contributionAudios.value = []
  existingContributionAudios.value = []
  selectedFiles.value = []
}

const openContributionDialog = async () => {
  contributionError.value = ''
  contributionSuccess.value = false

  // Only validate email and name for non-authenticated users
  if (!isAuthenticated.value) {
    if (!contributorName.value || contributorName.value.trim().length === 0) {
      contributionError.value = 'Please enter your name.'
      return
    }
    if (!contributorEmail.value || contributionEmailError.value) {
      contributionError.value = 'Please enter a valid email address.'
      return
    }
  }

  // If already authenticated, require message or photos
  if (isAuthenticated.value) {
    if (!contributorMessage.value && contributionPhotos.value.length === 0 && contributionAudios.value.length === 0 && existingContributionAudios.value.length === 0) {
      contributionError.value = 'Please include a message, photo, or voice memory.'
      return
    }
    contributionSubmitting.value = true
    signupToken.value = apiService.getToken()

    if (!signupToken.value) {
      contributionError.value = 'Authentication token is missing. Please refresh the page and try again.'
      contributionSubmitting.value = false
      return
    }

    if (!memorial.value?.shareSlug) {
      contributionError.value = 'Memorial information is missing. Please refresh the page and try again.'
      contributionSubmitting.value = false
      return
    }

    try {
      await submitContributionData()
    } catch (err) {
      contributionError.value = 'Unable to update your contribution. Please try again.'
      console.error('Contribution update error:', err)
    } finally {
      contributionSubmitting.value = false
    }
    return
  }

  contributionDialogStep.value = 'credentials'
  contributionDialogError.value = ''
  contributorPassword.value = ''
  contributorPasswordConfirm.value = ''
  verificationCode.value = ''
  signupToken.value = ''
  showContributionDialog.value = true
  contributionSubmitting.value = true
}

const closeContributionDialog = () => {
  if (signupLoading.value || verifyingCode.value || contributionSaving.value) return
  showContributionDialog.value = false
  contributionSubmitting.value = false
}

const startContributionSignup = async () => {
  if (contributorPassword.value.length < 6) {
    contributionDialogError.value = 'Password must be at least 6 characters long.'
    return
  }

  if (contributorPassword.value !== contributorPasswordConfirm.value) {
    contributionDialogError.value = 'Passwords do not match.'
    return
  }

  contributionDialogError.value = ''
  signupLoading.value = true

  try {
    const response = await apiService.signUp(contributorEmail.value, contributorPassword.value, null, contributorName.value)
    if (response.success || (response.pendingVerification && response.token)) {
      // Wait 500ms to allow server-side Hibernate flush to complete
      await sleep(500)

      // Both new signup and pending verification should proceed to verify step
      signupToken.value = response.token
      contributionDialogStep.value = 'verify'
    } else {
      contributionDialogError.value = response.msg || 'Unable to start verification. Please try again.'
    }
  } catch (err) {
    console.error('Contribution signup error:', err)
    contributionDialogError.value = 'Unable to start verification. Please try again.'
  } finally {
    signupLoading.value = false
  }
}

const submitContributionData = async () => {
  if (!signupToken.value || !memorial.value?.shareSlug) {
    throw new Error('Missing signup token or memory identifier')
  }

  contributionSaving.value = true
  contributionDialogError.value = ''

  try {
    const formData = new FormData()
    formData.append('shareSlug', memorial.value.shareSlug)
    formData.append('contributorName', contributorName.value || '')
    formData.append('contributorEmail', contributorEmail.value || '')
    formData.append('contributorMessage', contributorMessage.value || '')

    // Send removed photo IDs
    if (removedPhotoIds.value.length > 0) {
      removedPhotoIds.value.forEach(photoId => {
        formData.append('removedPhotoIds', photoId)
      })
    }

    // Send caption updates for existing photos
    contributionPhotos.value.forEach(photo => {
      if (photo.existing && photo.id) {
        formData.append('existingPhotoIds', photo.id)
        formData.append('existingPhotoCaptions', photo.caption || '')
      }
    })

    contributionAudios.value.forEach(audio => {
      if (audio.file) {
        formData.append('audios', audio.file)
        formData.append('audioCaptions', audio.caption || '')
      }
    })

    // Only submit new photos (not existing ones)
    contributionPhotos.value.forEach(photo => {
      if (!photo.existing && photo.file) {
        formData.append('photos', photo.file)
        formData.append('photoCaptions', photo.caption || '')
      }
    })

    const response = await apiService.submitContribution(formData, signupToken.value)
    if (response.success) {
      contributionSuccess.value = true
      contributionCompletedMessage.value = response.msg || 'Thank you for contributing.'
      contributionSuccessMessage.value = contributionCompletedMessage.value

      // For authenticated users, show success and reload
      if (isAuthenticated.value) {
        contributionError.value = ''
        contributionSubmitting.value = false

        // Reload the page after a short delay
        setTimeout(() => {
          //window.location.reload()
        }, 1500)
      } else {
        clearContributionForm()
        contributionDialogStep.value = 'success'
        contributionSubmitting.value = false

        // Reload the page after a short delay to show the contribution
        setTimeout(() => {
          //window.location.reload()
        }, 2000)
      }
    } else {
      contributionDialogError.value = response.msg || 'We were unable to save your contribution.'
      if (!isAuthenticated.value) {
        contributionDialogStep.value = 'verify'
      }
      contributionSubmitting.value = false
    }
  } catch (err) {
    console.error('Contribution submission error:', err)
    contributionDialogError.value = 'We were unable to save your contribution. Please try again.'
    if (!isAuthenticated.value) {
      contributionDialogStep.value = 'verify'
    }
    contributionSubmitting.value = false
  } finally {
    contributionSaving.value = false
  }
}

const verifyContributionCode = async () => {
  if (!verificationCode.value) {
    contributionDialogError.value = 'Enter the verification code that was emailed to you.'
    return
  }

  verifyingCode.value = true
  contributionDialogError.value = ''

  try {
    const response = await apiService.confirmCode(verificationCode.value, signupToken.value)
    if (response.success) {
      // Set the token to authenticate the user
      apiService.setToken(signupToken.value)

      // Mark that user just authenticated so we don't clear their token on page reload
      sessionStorage.setItem('contribution_authenticated', 'true')

      // Wait 500ms to allow server-side Hibernate flush to complete
      await sleep(500)

      // Submit the contribution data
      await submitContributionData()
    } else {
      contributionDialogError.value = response.msg || 'The verification code is incorrect.'
    }
  } catch (err) {
    console.error('Contribution verification error:', err)
    contributionDialogError.value = 'We could not verify the code. Please try again.'
  } finally {
    verifyingCode.value = false
  }
}

const startResendCooldown = () => {
  resendCooldown.value = 30
  resendTimer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimer)
      resendTimer = null
    }
  }, 1000)
}

const resendContributionCode = async () => {
  if (resendCooldown.value > 0 || resendingCode.value) return

  resendingCode.value = true
  contributionDialogError.value = ''

  try {
    const response = await apiService.resendCode(signupToken.value)
    if (response.success) {
      startResendCooldown()
    } else {
      contributionDialogError.value = response.msg || 'Unable to resend the code. Please try again.'
    }
  } catch (err) {
    console.error('Resend code error:', err)
    contributionDialogError.value = 'Unable to resend the code. Please try again.'
  } finally {
    resendingCode.value = false
  }
}

const resendDonorCode = async () => {
  if (resendCooldown.value > 0 || resendingCode.value) return

  resendingCode.value = true
  donationDialogError.value = ''

  try {
    const response = await apiService.resendCode(donorSignupToken.value)
    if (response.success) {
      startResendCooldown()
    } else {
      donationDialogError.value = response.msg || 'Unable to resend the code. Please try again.'
    }
  } catch (err) {
    console.error('Resend code error:', err)
    donationDialogError.value = 'Unable to resend the code. Please try again.'
  } finally {
    resendingCode.value = false
  }
}

// Donation functions
const onPresetAmountChange = () => {
  if (donationPresetAmount.value !== 'other') {
    donationCustomAmount.value = ''
  }
}

const initializeDonationStripe = async () => {
  try {
    const publishableKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY
    if (!publishableKey) {
      console.error('Stripe publishable key not configured')
      return
    }

    donationStripeInstance.value = await loadStripe(publishableKey)
    if (!donationStripeInstance.value) {
      console.error('Failed to load Stripe')
      return
    }

    const elements = donationStripeInstance.value.elements()
    donationCardElement.value = elements.create('card', {
      hidePostalCode: true,
      style: {
        base: {
          color: '#1f1f1f',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          '::placeholder': {
            color: 'var(--v-theme-neutral400)'
          }
        },
        invalid: {
          color: '#b00020'
        }
      }
    })

    donationCardElement.value.mount('#donation-card-element')
  } catch (error) {
    console.error('Stripe initialization error:', error)
    donationError.value = 'Failed to initialize payment system. Please refresh the page.'
  }
}

const processDonation = async () => {
  if (!donationAmount.value || parseFloat(donationAmount.value) <= 0) {
    donationError.value = 'Please enter a valid donation amount'
    return
  }

  // If not authenticated, show signup dialog first
  if (!isAuthenticated.value) {
    donationDialogStep.value = 'credentials'
    donationDialogError.value = ''
    donorName.value = ''
    donorEmail.value = ''
    donorPassword.value = ''
    donorPasswordConfirm.value = ''
    donorVerificationCode.value = ''
    donorSignupToken.value = ''
    showDonationDialog.value = true
    return
  }

  if (!donationStripeInstance.value || !donationCardElement.value) {
    donationError.value = 'Payment system not ready. Please refresh the page.'
    return
  }

  donationProcessing.value = true
  donationError.value = ''

  try {
    // Create payment method
    const { error: pmError, paymentMethod } = await donationStripeInstance.value.createPaymentMethod({
      type: 'card',
      card: donationCardElement.value
    })

    if (pmError) {
      donationError.value = pmError.message
      donationProcessing.value = false
      return
    }

    // Process donation through backend
    await submitDonationToBackend(paymentMethod.id)
  } catch (error) {
    console.error('Donation processing error:', error)
    donationError.value = error.message || 'An error occurred while processing your donation'
    donationProcessing.value = false
  }
}

const submitDonationToBackend = async (paymentMethodId) => {
  donationProcessing.value = true
  donationError.value = ''

  try {
    const response = await apiService.processDonation({
      memorialSlug: slug,
      amount: parseFloat(donationAmount.value),
      anonymous: donationAnonymous.value,
      paymentMethodId: paymentMethodId
    })

    if (response.success) {
      donationSuccess.value = true
      donationTransactionId.value = response.transactionId || response.paymentIntentId || 'N/A'
      donationCardElement.value.clear()
      showDonationDialog.value = false
    } else {
      donationError.value = response.msg || 'Donation failed. Please try again.'
      if (showDonationDialog.value) {
        donationDialogError.value = response.msg || 'Donation failed. Please try again.'
      }
    }
  } catch (error) {
    console.error('Donation submission error:', error)
    donationError.value = error.message || 'An error occurred while processing your donation'
    if (showDonationDialog.value) {
      donationDialogError.value = error.message || 'An error occurred while processing your donation'
    }
  } finally {
    donationProcessing.value = false
  }
}

const closeDonationDialog = () => {
  if (donorSignupLoading.value || donorVerifyingCode.value || donationProcessing.value) return
  showDonationDialog.value = false
  donationProcessing.value = false
  donorPaymentMethod.value = null
}

const loginDonor = async () => {
  if (!donorEmail.value || !donorEmail.value.includes('@')) {
    donationDialogError.value = 'Please enter a valid email address.'
    return
  }

  if (!donorPassword.value || donorPassword.value.length === 0) {
    donationDialogError.value = 'Please enter your password.'
    return
  }

  donationDialogError.value = ''
  donorSignupLoading.value = true

  try {
    // Use centralized login function to ensure proper authentication state
    const result = await authService.login(donorEmail.value, donorPassword.value)

    if (result.success) {
      // Mark that user just authenticated so we don't clear their token on page reload
      sessionStorage.setItem('contribution_authenticated', 'true')

      // Mark that we should scroll to donation section after reload
      sessionStorage.setItem('scroll_to_donation', 'true')

      // Close dialog and reload page to show user as logged in
      showDonationDialog.value = false
      donationDialogError.value = ''

      // Reload page to refresh authentication state
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } else {
      donationDialogError.value = result.error || 'Invalid email or password.'
    }
  } catch (err) {
    console.error('Donor login error:', err)
    donationDialogError.value = 'Unable to login. Please try again.'
  } finally {
    donorSignupLoading.value = false
  }
}

const startDonorSignup = async () => {
  if (!donorName.value || donorName.value.trim().length === 0) {
    donationDialogError.value = 'Please enter your name.'
    return
  }

  if (!donorEmail.value || !donorEmail.value.includes('@')) {
    donationDialogError.value = 'Please enter a valid email address.'
    return
  }

  if (donorPassword.value.length < 6) {
    donationDialogError.value = 'Password must be at least 6 characters long.'
    return
  }

  if (donorPassword.value !== donorPasswordConfirm.value) {
    donationDialogError.value = 'Passwords do not match.'
    return
  }

  donationDialogError.value = ''
  donorSignupLoading.value = true

  try {
    const response = await apiService.signUp(donorEmail.value, donorPassword.value, null, donorName.value)
    if (response.success || (response.pendingVerification && response.token)) {
      // Wait 500ms to allow server-side Hibernate flush to complete
      await sleep(500)

      // Both new signup and pending verification should proceed to verify step
      donorSignupToken.value = response.token
      donationDialogStep.value = 'verify'
    } else {
      donationDialogError.value = response.msg || 'Unable to start verification. Please try again.'
    }
  } catch (err) {
    console.error('Donor signup error:', err)
    donationDialogError.value = 'Unable to start verification. Please try again.'
  } finally {
    donorSignupLoading.value = false
  }
}

const verifyDonorCode = async () => {
  if (!donorVerificationCode.value) {
    donationDialogError.value = 'Enter the verification code that was emailed to you.'
    return
  }

  donorVerifyingCode.value = true
  donationDialogError.value = ''

  try {
    const response = await apiService.confirmCode(donorVerificationCode.value, donorSignupToken.value)
    if (response.success) {
      // Set the token to authenticate the user
      apiService.setToken(donorSignupToken.value)

      // Mark that user just authenticated so we don't clear their token on page reload
      sessionStorage.setItem('contribution_authenticated', 'true')

      // Mark that we should scroll to donation section after reload
      sessionStorage.setItem('scroll_to_donation', 'true')

      // Wait 500ms to allow server-side Hibernate flush to complete
      await sleep(500)

      // Create a contribution for the donor
      try {
        const formData = new FormData()
        formData.append('shareSlug', memorial.value.shareSlug)
        formData.append('contributorName', donorName.value || '')
        formData.append('contributorEmail', donorEmail.value || '')
        formData.append('contributorMessage', '') // Empty message for donors

        await apiService.submitContribution(formData, donorSignupToken.value)
      } catch (contributionErr) {
        console.error('Contribution creation error for donor:', contributionErr)
        // Don't block donation if contribution fails
      }

      // Close dialog and reload page to show user as logged in
      showDonationDialog.value = false
      donationDialogError.value = ''

      // Reload page to refresh authentication state
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } else {
      donationDialogError.value = response.msg || 'The verification code is incorrect.'
    }
  } catch (err) {
    console.error('Donor verification error:', err)
    donationDialogError.value = 'We could not verify the code. Please try again.'
  } finally {
    donorVerifyingCode.value = false
  }
}

onMounted(async () => {
  // Check if user just authenticated via contribution flow
  const justAuthenticated = sessionStorage.getItem('contribution_authenticated')

  if (justAuthenticated) {
    // User just completed verification and contributed - keep them logged in
    sessionStorage.removeItem('contribution_authenticated')
  } else if (!stayLoggedIn && !apiService.getToken()) {
    // Only clear authentication if there's no existing token
    // This prevents clearing authentication on normal page refreshes while allowing
    // fresh anonymous views when accessing from external links
    apiService.clearToken()
    localStorage.removeItem('memorial_auth')
  }

  try {
    const response = await apiService.getPublicMemorial(slug)
    handleMemorialResponse(response)

    // Initialize donation Stripe if memorial has funds
    if (response.success && response.memorial?.memorialFunds?.length > 0) {
      await initializeDonationStripe()

      // Check if we should scroll to donation section after login
      const shouldScrollToDonation = sessionStorage.getItem('scroll_to_donation')
      if (shouldScrollToDonation === 'true') {
        sessionStorage.removeItem('scroll_to_donation')
        // Wait for the page to fully render
        setTimeout(() => {
          const donationSection = document.getElementById('donation-section')
          if (donationSection) {
            donationSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 100)
      }
    }
  } catch (err) {
    console.error('Public memorial load error:', err)
    error.value = 'This memory might be private or the link may be incorrect. Please double-check the address or contact the memory creator.'
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  contributionPhotos.value.forEach(photo => {
    if (photo.preview) URL.revokeObjectURL(photo.preview)
  })
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
})
</script>

<style scoped>
.public-memorial {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
}

.main-card {
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 24px 45px -20px rgba(15, 23, 42, 0.2);
  background: var(--v-theme-surface);
}

.error-card {
  border: 1px solid rgba(239, 68, 68, 0.25);
}

.page-header {
  position: relative;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

/* Memorial Hero Section */
.memorial-hero {
  text-align: center;
  padding: 2rem 0;
}

.hero-photo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.hero-photo {
  width: 300px;
  height: 300px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.hero-photo-placeholder {
  width: 300px;
  height: 300px;
  background: var(--v-theme-gray100);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.hero-name {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem 0.75rem;
  font-family: var(--v-theme-headingFont);
  font-size: 2.5rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0 0 1rem 0;
}

.life-dates {
  color: var(--v-theme-gray500);
  font-size: 1.15rem;
  white-space: nowrap;
}

.hero-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.hero-detail-item {
  display: flex;
  align-items: center;
  color: var(--v-theme-gray500);
  font-size: 1rem;
}

@media (max-width: 600px) {
  .profile-menu-wrapper {
    right: -10px;
  }

  .hero-photo,
  .hero-photo-placeholder {
    width: 220px;
    height: 220px;
  }

  .hero-name {
    font-size: 1.75rem;
  }

  .hero-detail-item {
    font-size: 0.9rem;
  }
}

.return-visitor-card .v-card-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@media (min-width: 960px) {
  .return-visitor-card .v-card-text {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.section-title {
  font-weight: 600 !important;
  background: var(--v-theme-background);
  border-bottom: 1px solid var(--v-theme-border);
}

.rsvp-card,
.message-card,
.contribute-card {
  border: 1px solid var(--v-theme-border);
  border-radius: 16px;
  overflow: hidden;
}

.rsvp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.rsvp-option-btn {
  min-width: 220px;
  justify-content: flex-start;
  font-weight: 500;
  box-shadow: 0 10px 20px -12px rgba(37, 99, 235, 0.4);
  margin-right: 8px;
}

.rsvp-option-btn:last-child {
  margin-right: 0;
}

.contribute-intro {
  max-width: 640px;
}

.host-video-wrapper {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

.host-video-wrapper video {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
}

.host-message {
  max-width: 720px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.8;
}

.host-message.placeholder {
  color: rgba(15, 23, 42, 0.6);
}

.upload-area {
  border: 1px dashed rgba(59, 130, 246, 0.4);
  border-radius: 16px;
  padding: 24px;
  background: rgba(59, 130, 246, 0.05);
  position: relative;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  background: var(--v-theme-white);
  color: var(--v-theme-primary);
  border: 1px solid var(--v-theme-primary);
  border-radius: 999px;
  padding: 12px 20px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.upload-button:hover {
  transform: translateY(-1px);
  box-shadow: none;
}

.upload-hidden-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.photo-preview-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.photo-preview-row {
  display: flex;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--v-theme-border);
  border-radius: 16px;
  background: var(--v-theme-surface);
}

.preview-thumb img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 12px 24px -16px rgba(15, 23, 42, 0.4);
}

.preview-details {
  flex: 1;
  display: flex;
  align-items: flex-start;
}

.preview-details .v-textarea,
.preview-details .v-text-field,
.preview-details .v-input {
  margin-top: 0 !important;
}

.preview-actions {
  display: flex;
  align-items: flex-start;
}

@media (max-width: 600px) {
  .memorial-title {
    font-size: 1.75rem;
  }

  .return-visitor-card .v-card-text {
    align-items: flex-start;
    gap: 16px;
  }

  .photo-preview-row {
    flex-direction: column;
    align-items: stretch;
  }

  .preview-thumb img {
    width: 100%;
    height: 220px;
  }

  .rsvp-option-btn {
    min-width: 100%;
  }
}

/* Donation section styles */
.donation-profile-photo {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--v-theme-primary);
  flex-shrink: 0;
}

.stripe-card-element {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  background: white;
}

.stripe-card-element:focus-within {
  border-color: var(--v-theme-primary);
  box-shadow: 0 0 0 2px rgba(123, 214, 221, 0.1);
}

</style>
